import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    Optional,
    Output,
    ViewChild
} from '@angular/core';

import { catchError, combineLatest, filter, map, of, Subscription, throwError } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { ChartLoadingOptionsEnum } from '@Common/Charts/enums/chart-loading-options.enum';
import { ChartModel } from '@Common/Charts/models/chart.model';
import { ReportsSeriesDataMergerService } from '@Common/Reports/services/reports-series-data-merger.service';
import {
    newDataProcessingWorkflowBlockTypes,
    newDataProcessingWorkflowChartModelTypes
} from '@Common/Reports/models/new-data-processing-workflow-block-types.model';
import { BriefingImageSettingsModel } from '@Common/PageBuilder/models/briefing-image-settings.model';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';
import { ChartDisplayElementModel } from '../../../../Common/Reports/models/chart-display-element.model';
import { ChartSpecificTypesEnum } from '../../../../Common/Reports/enums/chart-specific-types.enum';
import { ReportsChartComponent } from '@Common/Charts/components/ReportsChart/reports-chart.component';
import { APP_BASE_HREF } from '@angular/common';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';

/**
 * Renders the am4chart off screen (not visible to user), then generates png image which is displayed.
 * This component also supports rendering the html charts (Coverage Summary & Top-term/post/mentions),
 * which is done off screen and converted to image.
 */
@Component({
    selector: 'agpr-briefing-chart',
    templateUrl: './briefing-chart.component.html',
    styleUrls: ['./briefing-chart.component.scss'],
    standalone: false
})
export class BriefingChartComponent extends ReportsChartComponent implements AfterViewInit, OnDestroy {
    @Input() id: string = null;
    @Input() smallIndicator: boolean = false;
    @Input() showLoadingUntil: ChartLoadingOptionsEnum = ChartLoadingOptionsEnum.all;
    @Input() settings: BriefingImageSettingsModel;
    @Input()
    set chart(chart: ChartModel) {
        if (this._chart && this._chart !== chart) {
            this.destroyOldChart();
        }
        this._chart = chart;
        this.initializeChart();
    }
    get chart() {
        return this._chart;
    }
    @Input() blockPosition: string;
    @Input() block: BlockModel;
    @Input() twigData: any = {};
    @Input() generateOnSend: boolean;
    /** this is used in backend error case */
    @Output() noData: EventEmitter<boolean> = new EventEmitter(false);
    @Output() sourceDeleted: EventEmitter<boolean> = new EventEmitter(false);

    /** this is used when backend returns zero results */
    @Output() noContent: EventEmitter<boolean> = new EventEmitter();

    @Output() generateImage: EventEmitter<any> = new EventEmitter();

    @ViewChild('chartElement', { static: true }) chartElement: ElementRef;

    initFrame: number;
    viewInitted: boolean = false;
    loading: boolean = false;

    _chart: ChartModel;
    watchDataSub: Subscription;

    constructor(
        public reportsSeriesDataMergerService: ReportsSeriesDataMergerService,
        public briefingsService: BriefingsService,
        public zone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        @Optional() localizationService?: LocalizationService,
        @Optional() currentUserStateService?: CurrentUserStateService,
    ) {
        super(
            reportsSeriesDataMergerService,
            zone,
            changeDetectorRef,
            appBaseHref,
            localizationService,
            currentUserStateService
        );
    }

    ngAfterViewInit() {
        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            this.viewInitted = true;
            this.initializeChart();
        });
    }

    setRenderedOutput(event: string): void {
        this.block.renderedOutput = event;
    }

    initializeChart() {
        if (!this.viewInitted) {
            return;
        }

        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            if (this._chart) {
                // eslint-disable-next-line max-len
                // HACK: for reports drilldown, whenever we change the source we have to create a new chart, and it always adds the new chart instead of replacing it
                while (this.chartElement.nativeElement.firstChild) {
                    this.chartElement.nativeElement.removeChild(this.chartElement.nativeElement.lastChild);
                }
                this.zone.runOutsideAngular(() => {
                    this._chart.setZone(this.zone);
                    this._chart.initChart(this.chartElement.nativeElement, this._chart.chartType);
                    if (this.watchDataSub) {
                        this.watchDataSub.unsubscribe();
                    }
                    this.watchDataSub = this.watchData(this._chart).subscribe({
                        next: async (complete) => {
                            if (!this._chart.amchart) {
                                return;
                            }
                            const displayElement = this.block.displayElement as BriefingChartDisplayElementModel;
                            this._chart.amchart.padding(5, 0, 5, 0);
                            if (this._chart.amchart.legend) {
                                this._chart.amchart.legend.padding(0, 0, 0, 0);
                            }

                            let waitTime = 0;
                            const chartSpecificType = ((this.block.displayElement as BriefingChartDisplayElementModel)
                                .chartBlock.displayElement as ChartDisplayElementModel).chartSpecificType;
                            if (chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
                                // wait for 800 ms to export the image as for word cloud, the animation takes some  time.
                                waitTime = 800;
                            }

                            let imageString;
                            let paddingLeft = displayElement?.styles?.padding?.paddingAll || 0;
                            let paddingRight = displayElement?.styles?.padding?.paddingAll || 0;
                            if (displayElement?.styles?.padding?.paddingOption) {
                                paddingLeft = displayElement?.styles?.padding?.leftPadding || 0;
                                paddingRight = displayElement?.styles?.padding?.rightPadding || 0;
                            }

                            let width = (this.briefingsService.getColumnWidth(this.blockPosition) - paddingLeft - paddingRight) *
                                displayElement.chart.widthPercent / 100 || null;

                            displayElement.chart.widthPx = width;
                            const oldChartUrl = displayElement.chart.chartUrl || displayElement.chart.oldChartUrl || null;
                            let height = displayElement.chart.heightPx || null;
                            let oldWidth = displayElement.chart.oldWidthPx || null;
                            let oldHeight = displayElement.chart.oldHeightPx || null;

                            // BE saves doubles instead of integers, so it will always be off when we set the width %
                            if (width) {
                                width = Math.floor(width);
                            }
                            if (height) {
                                height = Math.floor(height);
                            }
                            if (oldWidth) {
                                oldWidth = Math.floor(oldWidth);
                            }
                            if (oldHeight) {
                                oldHeight = Math.floor(oldHeight);
                            }

                            if ((oldWidth !== width) || (oldHeight !== height) || displayElement.chart.generateOnSend || displayElement.chart.refreshed ||
                                !displayElement.chartBlock.equals(displayElement.oldChartBlock)
                            ) {
                                displayElement.chart.oldWidthPx = width;
                                displayElement.chart.oldHeightPx = height;
                                setTimeout(async () => {
                                    imageString = await this._chart.getPNGImageData(
                                        1,
                                        displayElement.chart.widthPx,
                                        displayElement.chart.heightPx,
                                        true,
                                        true
                                    ) as string;

                                    (this.block.displayElement as BriefingChartDisplayElementModel).chart.refreshed = false;
                                    this.generateImage.emit(imageString);
                                    this.watchLoading();
                                }, waitTime);
                            } else {
                                this.generateImage.emit(oldChartUrl);
                                this.watchLoading();
                            }

                        }
                    });

                    this._chart.amchart.svgContainer.htmlElement.firstElementChild.setAttribute(
                        'style', 'width: 100%; height: 100%; position: relative; z-index: 500;'
                    );
                });
            }
        });

        // subscribe to a thing that tells us when it's ok to run the setup code
        this._chart?.ready.subscribe(value => {
            if (value) {
                this.setupSegmentAdapter();
            }
        });
    }

    /**
     * Gets data from the series to display into the chart
     */
    watchData(chart: ChartModel) {
        const data = chart.series.map(item => null);
        const previousData = chart.series.map(item => null);
        const erroredSeries = chart.series.map(() => false);

        const useNewDataMappingWorkflow = newDataProcessingWorkflowBlockTypes.includes(chart.metadata?.type)
            && newDataProcessingWorkflowChartModelTypes.some(x => chart instanceof x);

        const series$ = chart.series.map((item, index) => {
            const previousPeriodDataProvider$ = (item.hasPreviousPeriod) ? item.previousPeriodDataProvider
                .pipe(
                    filter(v => !!v),
                    map((itemData) => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = itemData;
                            if (useNewDataMappingWorkflow) {
                                return of(previousData);
                            } else {
                                chart.updateData(previousData.slice(), true);
                                if (chart.isContentEmpty && chart.isContentEmpty()) {
                                    this.noContent.emit(true);
                                } else {
                                    this.noContent.emit(false);
                                }
                                return of(null);
                            }

                        });
                    }),
                    catchError(err => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = [];
                            erroredSeries[index] = true;
                            if (!erroredSeries.includes(false)) {
                                this.noData.emit(true);
                                chart.amchart?.raiseCriticalError(err);
                            }
                        });
                        return throwError(() => new Error(err));
                    })
                ) : of([]);

            const currentPeriodDataProvider$ = item.dataProvider.pipe(
                filter(v => !!v),
                map(itemData => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = itemData;
                        if (chart.overrideNoContent) {
                            // sometimes this is needed because there is no previousDataSeries but existin in useNewDataMappingWorkflow
                            if (chart.isContentEmpty && chart.isContentEmpty()) {
                                this.noContent.emit(true);
                            } else {
                                this.noContent.emit(false);
                            }
                        }
                        if (useNewDataMappingWorkflow) {
                            return of(data);
                        } else {
                            chart.updateData(data.slice(), false);
                            if (chart.isContentEmpty && chart.isContentEmpty()) {
                                this.noContent.emit(true);
                            } else {
                                this.noContent.emit(false);
                            }
                            return of(null);
                        }
                    });
                }),
                catchError(err => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                            chart.amchart?.raiseCriticalError(err);
                        }
                        if (err.message === this.localizationService.transform('Briefings.error.chart.dataNotAvailable')) {
                            this.sourceDeleted.emit(true);
                        }
                    });
                    return throwError(() => new Error(err));
                })
            );

            return combineLatest([
                currentPeriodDataProvider$,
                previousPeriodDataProvider$
            ]).pipe(
                catchError((error) => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        previousData[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                            chart.amchart?.raiseCriticalError(error);
                        }
                    });

                    return of(null);
                }),
                tap(() => {
                    if (
                        useNewDataMappingWorkflow
                        && data.every(x => !!x)
                        && (!item.hasPreviousPeriod || previousData.every(x => !!x))
                    ) {
                        const mergedData = this.reportsSeriesDataMergerService.mergeSeriesData(
                            [item.hasPreviousPeriod ? previousData : [], data].flat(),
                            chart
                        );
                        chart.updateData(mergedData.slice(), true);
                        if (chart.isContentEmpty && chart.isContentEmpty()) {
                            this.noContent.emit(true);
                        } else {
                            this.noContent.emit(false);
                        }
                    }
                })
            );
        });


        return combineLatest(series$);
    }

    destroyOldChart() {
        this.loading = false;
        this._chart?.destroy();
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.initFrame);
        this.destroyOldChart();
        this.watchDataSub?.unsubscribe();
    }
}
